<script>
    import { isOnline, syncStatus, auth } from "../../stores.js";
    import { syncManager } from "../sync.js";
    import { db } from "../../db/index.js";
    import { push, link } from "svelte-spa-router";
    import DarkModeToggle from "./DarkModeToggle.svelte";
    import FontSelector from "./FontSelector.svelte";

    // Debug log - triggered only when isAuthenticated changes
    $: console.log("StatusBar - isAuthenticated changed:", $auth.isAuthenticated);

    $: user = $auth.user;
    $: userInitial = user?.name ? user.name.charAt(0).toUpperCase() : "?";

    async function handleManualSync() {
        console.log("handleManualSync called");

        const authData = await db.getAuth();
        console.log("authData:", authData);

        if (authData) {
            console.log("Starting fullSync...");
            const success = await syncManager.fullSync();
            console.log("fullSync completed, success:", success);

            // Dispatch event to notify that sync is complete
            window.dispatchEvent(new CustomEvent("syncComplete"));
            console.log("syncComplete event dispatched");
        } else {
            console.error("No auth data found");
        }
    }

    async function handleLogout() {
        const success = await auth.logout();
        if (success) {
            push("/login");
        }
    }
</script>

<div class="navbar bg-base-200 border-b border-base-300 min-h-0 px-4 py-2">
    <!-- App icon -->
    <div class="flex-none mr-2">
        <a href="/" use:link class="btn btn-ghost btn-circle p-1">
            <img src="/icon-source.svg" alt="Accueil" class="w-8 h-8 rounded-lg" />
        </a>
    </div>

    <!-- Online/Offline indicator -->
    <div class="flex-none">
        <div class="badge badge-lg gap-2">
            <span class="relative flex h-2 w-2">
                {#if $isOnline}
                    <span class="animate-ping absolute inline-flex h-full w-full rounded-full opacity-75 bg-green-500"></span>
                    <span class="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
                {:else}
                    <span class="relative inline-flex rounded-full h-2 w-2 bg-orange-500"></span>
                {/if}
            </span>
            <span class="text-xs font-medium">
                {$isOnline ? "En ligne" : "Hors ligne"}
            </span>
        </div>
    </div>

    <!-- Sync status -->
    {#if $auth.isAuthenticated}
        <div class="flex-none ml-2">
            <button
                class="btn btn-sm btn-outline gap-2"
                on:click={handleManualSync}
                disabled={$syncStatus.isSyncing || !$isOnline}
                title={$isOnline
                    ? "Synchroniser maintenant"
                    : "Synchronisation impossible hors ligne"}
            >
                <svg
                    class="w-4 h-4 {$syncStatus.isSyncing ? 'animate-spin' : ''}"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                >
                    <path
                        d="M21.5 2v6h-6M2.5 22v-6h6M2 11.5a10 10 0 0 1 18.8-4.3M22 12.5a10 10 0 0 1-18.8 4.2"
                    />
                </svg>

                {#if $syncStatus.isSyncing}
                    <span class="text-xs">Sync...</span>
                {:else if $syncStatus.pendingCount > 0}
                    <span class="text-xs">{$syncStatus.pendingCount} en attente</span>
                    <div class="badge badge-primary badge-xs">{$syncStatus.pendingCount}</div>
                {:else}
                    <span class="text-xs">Synchronisé</span>
                {/if}
            </button>
        </div>
    {/if}

    <!-- User menu -->
    {#if $auth.isAuthenticated && user}
        <div class="flex-1"></div>
        <div class="flex-none">
            <div class="dropdown dropdown-end">
                <div tabindex="0" role="button" class="btn btn-ghost btn-circle avatar">
                    <div class="w-9 rounded-full bg-primary text-primary-content flex items-center justify-center">
                        <span class="text-sm font-semibold">{userInitial}</span>
                    </div>
                </div>
                {console.log("StatusBar - Rendering user menu button")}

                <ul tabindex="0" class="menu menu-sm dropdown-content bg-base-100 rounded-box z-[9999] mt-3 w-64 p-2 shadow-lg border border-base-300">
                    {console.log("StatusBar - Rendering dropdown")}
                    <li class="menu-title">
                        <div class="px-2 py-3">
                            <div class="font-semibold text-base">{user.name}</div>
                            {#if $syncStatus.lastSync}
                                <div class="text-xs opacity-60 mt-1">
                                    Dernière synchro: {new Date(
                                        $syncStatus.lastSync,
                                    ).toLocaleString("fr-FR", {
                                        day: "2-digit",
                                        month: "2-digit",
                                        year: "numeric",
                                        hour: "2-digit",
                                        minute: "2-digit",
                                    })}
                                </div>
                            {/if}
                        </div>
                    </li>
                    <div class="divider my-0"></div>
                    <li>
                        <div class="flex items-center justify-between py-2">
                            <DarkModeToggle />
                        </div>
                    </li>
                    <div class="divider my-0"></div>
                    <li>
                        <div class="flex items-center justify-between py-2">
                            <FontSelector />
                        </div>
                    </li>
                    <div class="divider my-0"></div>
                    <li>
                        <a href="/student" class="flex items-center gap-2" target="_blank" rel="noopener noreferrer">
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="18"
                                height="18"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                stroke-width="2"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                            >
                                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                            </svg>
                            Éditeur de contenu
                        </a>
                    </li>
                    <div class="divider my-0"></div>
                    <li>
                        <a href="/app#/bug-reports/new" class="flex items-center gap-2">
                            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                            Signaler un problème
                        </a>
                    </li>
                    <li>
                        <a href="/app#/bug-reports" class="flex items-center gap-2">
                            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" viewBox="0 0 24 24"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
                            Mes signalements
                        </a>
                    </li>
                    <div class="divider my-0"></div>
                    <li>
                        <button class="text-error" on:click={handleLogout}>
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="18"
                                height="18"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                stroke-width="2"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                            >
                                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"
                                ></path>
                                <polyline points="16 17 21 12 16 7"></polyline>
                                <line x1="21" y1="12" x2="9" y2="12"></line>
                            </svg>
                            Déconnexion
                        </button>
                    </li>
                </ul>
            </div>
        </div>
    {:else}
        {console.log(
            "StatusBar - User menu NOT rendered. isAuthenticated:",
            $auth.isAuthenticated,
            "user:",
            user,
        )}
        <div class="flex-1"></div>
        <!-- Logout button for unauthenticated state -->
        <div class="flex-none">
            <button
                class="btn btn-ghost btn-circle text-error"
                on:click={handleLogout}
                title="Forcer la déconnexion"
            >
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                >
                    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
                    <polyline points="16 17 21 12 16 7"></polyline>
                    <line x1="21" y1="12" x2="9" y2="12"></line>
                </svg>
            </button>
        </div>
    {/if}
</div>
